import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col" };
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { AutoComplete, AutoCompleteAllResultsModal } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Shared entity picker (campaign / contact / organisation) for the donation flows: an OPAL
 * AutoComplete plus the dropdown-footer affordances — "Voir tous les résultats (n)" (opens the
 * all-results browser) and an optional "+ Créer …" link — and the all-results modal itself. The data
 * source (options/loading/total + the search & fetch-all functions) is owned by the caller's
 * `use*Options` composable; this component only renders the shell and relays selection/create intent,
 * so the donation detail tab and the create modal share one selector behaviour.
 */
const SEE_ALL_THRESHOLD = 5;
// With no query the DB total is the meaningful count ("browse all 6324"); once the user types, the
// honest count is the server total for that query when the caller searches server-side
// (`searchTotalCount`), else the filtered buffer length — either way the button hides when nothing
// matches.
export default /*@__PURE__*/ _defineComponent({
    __name: 'FinanceEntityPicker',
    props: {
        modelValue: {},
        label: {},
        placeholder: { default: undefined },
        options: {},
        loading: { type: Boolean, default: false },
        error: { default: undefined },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        dataTestid: { default: undefined },
        totalCount: { default: 0 },
        searchTotalCount: { default: undefined },
        fetchAllResults: { type: Function, default: undefined },
        createLabelKey: { default: undefined }
    },
    emits: ["update:modelValue", "search", "select", "create"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        function seeAllCount(query) {
            if (!query.trim())
                return props.totalCount;
            return props.searchTotalCount ?? props.options.length;
        }
        function seeAllLabel(query) {
            if (!props.fetchAllResults || seeAllCount(query) <= SEE_ALL_THRESHOLD)
                return undefined;
            return t("finance.donations.create.seeAllResults", { count: seeAllCount(query) });
        }
        function createLabel(query) {
            return props.createLabelKey ? t(props.createLabelKey, { name: query.trim() }) : undefined;
        }
        function onUpdate(value) {
            const id = value ?? "";
            emit("update:modelValue", id);
            const option = props.options.find((o) => o.value === id);
            emit("select", option ?? { value: id, label: id });
        }
        // ── See-all browser ──────────────────────────────────────────────────────────
        const showAllResultsModal = ref(false);
        const allResultsQuery = ref("");
        const allResultsOptions = ref([]);
        const allResultsLoading = ref(false);
        const allResultsError = ref(false);
        async function openAllResults(query) {
            if (!props.fetchAllResults)
                return;
            allResultsQuery.value = query;
            showAllResultsModal.value = true;
            allResultsError.value = false;
            allResultsLoading.value = true;
            try {
                allResultsOptions.value = await props.fetchAllResults(query);
            }
            catch (err) {
                logger.error("FinanceEntityPicker: see-all fetch failed", err);
                allResultsOptions.value = [];
                allResultsError.value = true;
            }
            finally {
                allResultsLoading.value = false;
            }
        }
        function onAllResultsConfirm(option) {
            emit("update:modelValue", option.value);
            emit("select", option);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                    "model-value": __props.modelValue,
                    label: __props.label,
                    placeholder: __props.placeholder,
                    options: __props.options,
                    loading: __props.loading,
                    error: __props.error,
                    required: __props.required,
                    disabled: __props.disabled,
                    "icon-left": '',
                    "icon-right": '',
                    "data-testid": __props.dataTestid,
                    "full-width": "",
                    "see-all-label": seeAllLabel,
                    "create-label": createLabel,
                    onSearch: _cache[0] || (_cache[0] = ($event) => (emit('search', $event))),
                    "onUpdate:modelValue": onUpdate,
                    onSeeAll: openAllResults,
                    onCreate: _cache[1] || (_cache[1] = ($event) => (emit('create', $event.trim())))
                }), null, 16 /* FULL_PROPS */, ["model-value", "label", "placeholder", "options", "loading", "error", "required", "disabled", "data-testid"]),
                (__props.fetchAllResults)
                    ? (_openBlock(), _createBlock(_unref(AutoCompleteAllResultsModal), {
                        key: 0,
                        modelValue: showAllResultsModal.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showAllResultsModal).value = $event)),
                        options: allResultsOptions.value,
                        loading: allResultsLoading.value,
                        error: allResultsError.value,
                        title: _ctx.$t('finance.donations.create.allResults.title', {
                            count: allResultsOptions.value.length,
                            query: allResultsQuery.value,
                        }),
                        subtitle: _ctx.$t('finance.donations.create.allResults.subtitle'),
                        "close-label": _ctx.$t('finance.donations.create.allResults.close'),
                        "select-label": _ctx.$t('finance.donations.create.allResults.select'),
                        "load-error-label": _ctx.$t('finance.donations.create.allResults.loadError'),
                        "empty-label": _ctx.$t('finance.donations.create.allResults.empty'),
                        onConfirm: onAllResultsConfirm
                    }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
